/**
 * Strategy answer helpers — seed from payload and pick by stage.
 *
 * Belongs here: answer-map transforms for strategy questionnaire.
 * Does not belong here: HTTP calls (see api/useSubmitStrategyAnswersMutation.ts).
 */
import type {
  StrategyQuestionnairePayload,
  StrategyQuestionnaireAnswersMap
} from '../shared/types/questionnaire.types'

/**
 * Keep only answers for questions that belong to `stageId`.
 * Used when POSTing a single-stage edit (e.g. hub SIP complete).
 */
export function pickAnswersForStage(
  answers: StrategyQuestionnaireAnswersMap,
  stageId: string,
  stages: StrategyQuestionnairePayload['stages'] | undefined
): StrategyQuestionnaireAnswersMap {
  const stage = (stages ?? []).find((stageItem) => stageItem?.id === stageId)
  const questionIds = new Set(
    (stage?.questions ?? []).map((question) => question?.id).filter(Boolean)
  )
  const picked: StrategyQuestionnaireAnswersMap = {}
  for (const [questionId, value] of Object.entries(answers ?? {})) {
    if (questionIds.has(questionId)) picked[questionId] = value
  }
  return picked
}

/** Seeds draft answers from API payload (saved responses on questionnaire). */
export function buildInitialStrategyAnswersFromPayload(
  payload: StrategyQuestionnairePayload
): StrategyQuestionnaireAnswersMap {
  const answersMap: StrategyQuestionnaireAnswersMap = {}
  const stages = payload?.stages ?? []
  for (const stage of stages) {
    for (const question of stage?.questions ?? []) {
      const questionId = question?.id
      if (!questionId || !question?.input) continue
      const inputType = question.input?.type
      // Only seed when API already has a saved response for this question
      if (inputType === 'AMOUNT') {
        const numericValue = question.input?.inputDataNumber
        if (typeof numericValue === 'number' && !Number.isNaN(numericValue)) {
          answersMap[questionId] = numericValue
        }
      } else {
        const selectedOptionIds = question.input?.selectedOptionIds
        if (Array.isArray(selectedOptionIds) && selectedOptionIds.length > 0) {
          answersMap[questionId] =
            selectedOptionIds.length === 1 ? selectedOptionIds[0] : [...selectedOptionIds]
        }
      }
    }
  }
  return answersMap
}
